// src/components/pages/CourseCurriculum.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "../Styles/LmsExtra.css";
import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronUp,
  FiEye,
  FiTrash2,
  FiEdit2,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiHelpCircle,
  FiClipboard,
} from "react-icons/fi";

import { API_BASE } from "../../../api";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import { getSessionUserId } from "../../../utils/SessionUser";
import { ENDPOINTS as SEC_ENDPOINTS } from "../../Seguridad/Utils/Endpoints";
import { cursosNormalize } from "../Objetos/CursosModel";
import { moduloNormalize } from "../Objetos/ModuloModel";
import { leccionesNormalize } from "../Objetos/LeccionesModel";
import ConfirmDeleteDialog from "../../security/ConfirmDeleteDialog";
import AlertDialog from "../Components/Shared/AlertDialog";
import { getExamAttempts } from "../Utils/ExamResponseService";
import { getQuizAttempts } from "../Utils/QuizResponseService";
import { calculateCourseProgress, calculateAverageExamGrade, getCompletedLessons, getFailedLessons, getQuizzesStatus, getExamsStatus } from "../Utils/ProgressService";

const CourseCurriculum = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // ---------- State principal ----------
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [expandedModules, setExpandedModules] = useState({});

  // Estado para calificación y comentario
  const [userRating, setUserRating] = useState(0);
  const [userComment, setUserComment] = useState("");
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  // Edición de títulos (curso / módulo / lección)
  const [editingCourseTitle, setEditingCourseTitle] = useState(false);
  const [courseTitleDraft, setCourseTitleDraft] = useState("");

  const [editingModuleId, setEditingModuleId] = useState(null);
  const [moduleTitleDraft, setModuleTitleDraft] = useState("");

  const [editingLesson, setEditingLesson] = useState(null); // { moduleId, lessonId }
  const [lessonTitleDraft, setLessonTitleDraft] = useState("");

  // Estados para diálogos de eliminación
  const [deleteModuleDialog, setDeleteModuleDialog] = useState({
    open: false,
    moduleId: null,
    moduleName: "",
  });
  const [deleteLessonDialog, setDeleteLessonDialog] = useState({
    open: false,
    moduleId: null,
    lessonId: null,
    lessonName: "",
  });
  const [deleteQuizDialog, setDeleteQuizDialog] = useState({
    open: false,
    moduleId: null,
    quizId: null,
    quizName: "",
  });
  const [deleteExamDialog, setDeleteExamDialog] = useState({
    open: false,
    moduleId: null,
    examId: null,
    examName: "",
  });

  // Edición de cuestionarios
  const [editingQuiz, setEditingQuiz] = useState(null); // { moduleId, quizId }
  const [quizTitleDraft, setQuizTitleDraft] = useState("");

  // Edición de exámenes
  const [editingExam, setEditingExam] = useState(null); // { moduleId, examId }
  const [examTitleDraft, setExamTitleDraft] = useState("");

  // Drag & Drop para reordenar contenido
  const [draggedItem, setDraggedItem] = useState(null); // { moduleId, item }
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Modal "Nuevo" (Lección / Cuestionario / Evaluación)
  const [newContentModal, setNewContentModal] = useState({
    open: false,
    moduleId: null,
  });

  // Estados para control de límite de intentos
  const [idEstudiante, setIdEstudiante] = useState(null);
  const [idEmpleado, setIdEmpleado] = useState(null);
  const [estudianteCursoRow, setEstudianteCursoRow] = useState(null);
  const [allAttempts, setAllAttempts] = useState({}); // { 'EXAMEN-5': 2, 'CUESTIONARIO-8': 1 }

  const [studentCertificateUrl, setStudentCertificateUrl] = useState(null);
  const [certificateCongratsOpen, setCertificateCongratsOpen] = useState(false);
  const [certificateCongratsDismissed, setCertificateCongratsDismissed] = useState(false);

  // Estados para progreso del curso
  const [courseProgress, setCourseProgress] = useState({ completadas: 0, total: 0, porcentaje: 0 });
  const [completedLessonsIds, setCompletedLessonsIds] = useState([]); // IDs de lecciones completadas
  const [failedLessonsIds, setFailedLessonsIds] = useState([]); // IDs de elementos reprobados
  const [averageExamGrade, setAverageExamGrade] = useState({ promedio: 0, totalExamenes: 0 });

  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

  // ---------- Función para cargar curso completo desde BD ----------
  const loadCursoCompleto = async () => {
    if (!slug) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // 1. Cargar curso y sus componentes EN PARALELO y FILTRADOS por backend (Optimizado)
      const courseId = Number(slug);
      const [respCurso, respModulos, respLecciones, respCuestionarios, respExamenes, respModuloContenido] = await Promise.all([
        axios.get(LMS_ENDPOINTS.cursosById(courseId)),
        axios.get(LMS_ENDPOINTS.moduloByCurso(courseId)),
        axios.get(LMS_ENDPOINTS.leccionesByCurso(courseId)),
        axios.get(LMS_ENDPOINTS.cuestionarioByCurso(courseId)),
        axios.get(LMS_ENDPOINTS.examenByCurso(courseId)),
        axios.get(LMS_ENDPOINTS.moduloContenidoByCurso(courseId))
      ]);

      // Procesar Curso
      const cursoDataRaw = respCurso.data;
      const cursoData = Array.isArray(cursoDataRaw) ? cursoDataRaw[0] : cursoDataRaw;

      let cursoEncontrado = null;
      if (cursoData) {
        cursoEncontrado = cursosNormalize(cursoData);
      }

      if (!cursoEncontrado) {
        console.warn("Curso no encontrado en la lista de BD");
        setLoading(false);
        return;
      }

      // Procesar Módulos
      const modulosDelCurso = (respModulos.data || [])
        .map(moduloNormalize)
        .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

      // Procesar Contenidos
      const todasLecciones = (respLecciones.data || []).map(leccionesNormalize);
      const todosCuestionarios = respCuestionarios.data || [];
      const todosExamenes = respExamenes.data || [];
      const todoModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);

      // console.log("📦 ModuloContenido cargado:", todoModuloContenido);

      // 4. Mapear módulos con su contenido ORDENADO según ModuloContenido
      const modulosMapeados = modulosDelCurso.map((mod) => {
        // Obtener ModuloContenido de este módulo
        const contenidosDelModulo = todoModuloContenido
          .filter((mc) => mc.idModulo === mod.idModulo && mc.activo)
          .sort((a, b) => {
            const ordenA = a.orden ?? a.Orden ?? 0;
            const ordenB = b.orden ?? b.Orden ?? 0;
            return ordenA - ordenB;
          });

        // Mapear cada contenido a su tipo específico
        const contenidoMapeado = contenidosDelModulo.map((mc) => {
          const tipo = mc.tablaContenido;

          if (tipo === "Lecciones") {
            const leccion = todasLecciones.find(l => l.idLeccion === mc.idAsociado);
            if (!leccion) {
              console.warn(`⚠️ Lección ${mc.idAsociado} no encontrada`);
              return null;
            }

            // Validar que la lección esté activa
            if (!(leccion.activo ?? leccion.Activo ?? true)) return null;

            return {
              tipo: "LECCION",
              id: leccion.idLeccion,
              idLeccion: leccion.idLeccion,
              idModulo: leccion.idModulo,
              titulo: leccion.titulo,
              monedas: leccion.monedas,
              activo: leccion.activo,
              completado: false, // Requiere LeccionEstudiante
              idModuloContenido: mc.idModuloContenido
            };
          }

          if (tipo === "Cuestionario") {
            const cuestionario = todosCuestionarios.find(
              q => (q.idCuestionario || q.IdCuestionario) === mc.idAsociado
            );
            if (!cuestionario) {
              console.warn(`⚠️ Cuestionario ${mc.idAsociado} no encontrado`);
              return null;
            }

            // Validar que esté activo
            const itemActivo = cuestionario.activo !== undefined ? cuestionario.activo : cuestionario.Activo;
            if (itemActivo === false) return null;

            return {
              tipo: "CUESTIONARIO",
              id: cuestionario.idCuestionario || cuestionario.IdCuestionario,
              idCuestionario: cuestionario.idCuestionario || cuestionario.IdCuestionario,
              titulo: cuestionario.nombre || cuestionario.Nombre,
              activo: cuestionario.activo !== undefined ? cuestionario.activo : cuestionario.Activo,
              limiteIntentos: cuestionario.limiteIntentos !== undefined ? cuestionario.limiteIntentos : cuestionario.LimiteIntentos,
              idModuloContenido: mc.idModuloContenido
            };
          }

          if (tipo === "Examen") {
            const examen = todosExamenes.find(
              e => (e.idExamen || e.IdExamen) === mc.idAsociado
            );
            if (!examen) {
              console.warn(`⚠️ Examen ${mc.idAsociado} no encontrado`);
              return null;
            }

            // Validar que esté activo
            const examActivo = examen.activo !== undefined ? examen.activo : examen.Activo;
            if (examActivo === false) return null;

            return {
              tipo: "EXAMEN",
              id: examen.idExamen || examen.IdExamen,
              idExamen: examen.idExamen || examen.IdExamen,
              titulo: examen.nombre || examen.Nombre,
              activo: examen.activo !== undefined ? examen.activo : examen.Activo,
              limiteIntentos: examen.limiteIntentos !== undefined ? examen.limiteIntentos : examen.LimiteIntentos,
              idModuloContenido: mc.idModuloContenido
            };
          }

          console.warn(`⚠️ Tipo de contenido no soportado: ${tipo}`);
          return null;
        }).filter(Boolean); // Eliminar nulls

        return {
          id: mod.idModulo,
          idModulo: mod.idModulo,
          idCurso: mod.idCurso,
          titulo: mod.nombre,
          nombre: mod.nombre,
          orden: mod.orden,
          activo: mod.activo,
          contenido: contenidoMapeado, // Array unificado de lecciones, cuestionarios, etc.
          // Mantener compatibilidad con código existente
          lecciones: contenidoMapeado.filter(c => c.tipo === "LECCION")
        };
      });

      // 5. Contar cuestionarios totales del curso
      const cuestionariosDelCurso = todosCuestionarios.filter(
        (q) => (q.idCurso || q.IdCurso) === cursoEncontrado.idCurso
      );

      const cuestionariosMapeados = cuestionariosDelCurso.map((q) => ({
        idCuestionario: q.idCuestionario || q.IdCuestionario,
        idCurso: q.idCurso || q.IdCurso,
        nombre: q.nombre || q.Nombre,
        activo:
          q.activo !== undefined
            ? q.activo
            : q.Activo !== undefined
              ? q.Activo
              : true,
      }));

      setCourse({
        ...cursoEncontrado,
        titulo: cursoEncontrado.nombre,
        duracionSemanas: Math.ceil(cursoEncontrado.duracion / 40) || 1,
        estudiantes: 0, // Placeholder
        cuestionarios: cuestionariosMapeados.length,
        cuestionariosDetalle: cuestionariosMapeados,
        modulos: modulosMapeados,
      });
      setError(null);
    } catch (err) {
      console.error("Error al cargar curso completo desde BD:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // ---------- useEffect: Cargar curso al montar ----------
  useEffect(() => {
    loadCursoCompleto();
  }, [slug]);

  // Mantener expandedModules sincronizado con los módulos actuales
  useEffect(() => {
    if (!course) return;
    setExpandedModules((prev) => {
      const next = { ...prev };
      (course.modulos || []).forEach((m) => {
        if (!(m.id in next)) {
          next[m.id] = true;
        }
      });
      return next;
    });
  }, [course]);

  // Cargar ID del estudiante basado en el usuario logueado ACTUAL
  useEffect(() => {
    const loadEstudianteId = async () => {
      const courseId = Number(slug);
      if (!courseId) return;

      const userId = getSessionUserId();
      if (!userId) {
        console.warn("[CourseCurriculumStudent] No hay usuario logueado en sesión");
        return;
      }

      try {
        // console.log(`[CourseCurriculumStudent] Identificando estudiante para usuario ${userId}...`);

        // 1. Intentar obtener el estudiante directamente por ID de usuario
        let estudianteId = null;
        let empleadoId = null;

        try {
          const resEstById = await axios.get(LMS_ENDPOINTS.estudianteByUsuario(userId));
          if (resEstById.data) {
            const data = Array.isArray(resEstById.data) ? resEstById.data[0] : resEstById.data;
            if (data) {
              const est = Models.estudiante(data);
              estudianteId = est.idEstudiante;
              empleadoId = est.idEmpleado;
            }
          }
        } catch (err) {
          console.warn("[CourseCurriculumStudent] Error al obtener estudiante por usuario directo, intentando búsqueda manual...");
        }

        // 2. Si falló el directo, buscar manualmente (Fallback robusto)
        if (!estudianteId) {
          const [resUsuarios, resEmpleados, resEstudiantes] = await Promise.all([
            axios.get(LMS_ENDPOINTS.usuarios),
            axios.get(LMS_ENDPOINTS.empleados),
            axios.get(LMS_ENDPOINTS.estudiante)
          ]);

          const usuario = (resUsuarios.data || []).find(u => String(u.idUsuario || u.IdUsuario) === String(userId));
          if (usuario) {
            const idPersona = usuario.idPersona || usuario.IdPersona;
            const empleado = (resEmpleados.data || []).find(e => String(e.idPersona || e.IdPersona) === String(idPersona));
            if (empleado) {
              empleadoId = empleado.idEmpleado || empleado.IdEmpleado;
              const miEstudiante = (resEstudiantes.data || []).map(Models.estudiante).find(est => String(est.idEmpleado) === String(empleadoId));
              if (miEstudiante) {
                estudianteId = miEstudiante.idEstudiante;
              }
            }
          }
        }

        if (estudianteId) {
          // console.log(`[CourseCurriculumStudent] Estudiante identificado: ${estudianteId}`);
          setIdEstudiante(estudianteId);
          setIdEmpleado(empleadoId);

          // 3. Buscar relación específica de ESTE estudiante con el curso
          const response = await axios.get(LMS_ENDPOINTS.estudiantesCurso);
          const relaciones = (response.data || []).map(Models.estudiantesCurso);

          const relacionCurso = relaciones.find(
            r => (r.idCurso || r.IdCurso) === courseId &&
              String(r.idEstudiante || r.IdEstudiante) === String(estudianteId)
          );

          if (relacionCurso) {
            // console.log("[CourseCurriculumStudent] Registro de inscripción encontrado");
            setEstudianteCursoRow(relacionCurso);
          } else {
            // console.log("[CourseCurriculumStudent] Estudiante no inscrito en este curso todavía");
            setEstudianteCursoRow(null);
          }
        } else {
          console.warn("[CourseCurriculumStudent] No se pudo resolver la identidad de estudiante");
        }

      } catch (error) {
        console.error('[CourseCurriculumStudent] Error cargando identidad de estudiante:', error);
      }
    };

    loadEstudianteId();
  }, [slug]);

  // Ya no necesitamos loadEmpleadoId aparte porque lo resolvemos arriba

  const canDownloadCertificate = useMemo(() => {
    if (!course) return false;
    if (!course.modulos) return false;

    const totalLessons = course.modulos.reduce(
      (acc, m) => acc + (m.contenido || []).filter((x) => x.tipo === 'LECCION').length,
      0
    );

    const completedLessons = completedLessonsIds.filter((k) => String(k).startsWith('LECCION-')).length;
    const allLessonsCompleted = totalLessons > 0 && completedLessons >= totalLessons;

    const avgOk = Number(averageExamGrade.promedio || 0) >= 60;

    return allLessonsCompleted && avgOk;
  }, [course, completedLessonsIds, averageExamGrade]);

  const certificadoGenerado = Boolean(estudianteCursoRow?.certificadoGenerado);

  useEffect(() => {
    setCertificateCongratsOpen(false);
    setCertificateCongratsDismissed(false);
  }, [slug]);

  useEffect(() => {
    if (certificadoGenerado) {
      setCertificateCongratsOpen(false);
      return;
    }

    if (canDownloadCertificate && !certificateCongratsDismissed) {
      setCertificateCongratsOpen(true);
    }
  }, [canDownloadCertificate, certificateCongratsDismissed, certificadoGenerado]);

  const showGenerateCertificateButton =
    canDownloadCertificate && !certificadoGenerado && certificateCongratsDismissed;

  const handleSubmitRatingAndCertificate = async () => {
    if (userRating === 0) {
      setAlertDialog({
        open: true,
        title: 'Atención',
        message: "Por favor, califica el curso para continuar.",
        variant: 'warning'
      });
      return;
    }
    setRatingSubmitting(true);
    try {
      // 1. Guardar calificación
      const ratingDTO = {
        IdCurso: Number(slug),
        IdEstudiante: Number(idEstudiante),
        Calificacion: userRating,
        Comentario: userComment,
        Activo: true,
        FechaRegistro: new Date().toISOString()
      };

      // Nota: Asegúrate de que LMS_ENDPOINTS tenga 'cursoRating'
      await axios.post(LMS_ENDPOINTS.cursoRating, ratingDTO);
      console.log("✅ Calificación guardada");

      // 2. Generar certificado (Llamamos a la función original con flag true)
      await handleDownloadCertificate(true);

    } catch (error) {
      console.error("Error al guardar calificación o certificado:", error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: "Ocurrió un error al procesar tu solicitud. Intenta nuevamente.",
        variant: 'error'
      });
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handleDownloadCertificate = async (skipRatingCheck = false) => {
    // Si se llama directamente y no se ha calificado (y no es skipRatingCheck), indicar error o abrir modal
    // Pero la UI controla el acceso via modal ahora.
    // Mantenemos esta lógica por seguridad si se llama desde otro lado.
    if (!skipRatingCheck && userRating === 0 && !certificadoGenerado) {
      // En lugar de alertar, abrimos el modal si está cerrado, o no hacemos nada y dejamos que la UI lo maneje.
      // Asumimos que la UI llama a handleSubmitRatingAndCertificate
    }

    const courseId = Number(slug);
    if (!courseId) return;
    if (!idEmpleado || !idEstudiante) return;
    // if (!canDownloadCertificate) return; // Validación relajada si ya estamos aquí

    try {
      // Usar endpoint histórico para asegurar que encontramos la certificación aunque el curso esté inactivo
      const resCerts = await axios.get(LMS_ENDPOINTS.certificacionesByCursoHistorico(courseId));
      const cert = (resCerts.data || []).map(Models.certificaciones)[0];

      if (!cert?.idCertificacion) {
        setAlertDialog({
          open: true,
          title: 'Configuración de Certificado',
          message: 'No se encontró la certificación de este curso.',
          variant: 'warning'
        });
        return;
      }

      const res = await axios.post(LMS_ENDPOINTS.certificadosGenerate, {
        IdCertificacion: cert.idCertificacion,
        IdEstudiante: idEmpleado,
      });

      const url = res?.data?.certificateUrl || null;
      if (!url) {
        setAlertDialog({
          open: true,
          title: 'Error de Generación',
          message: 'No se pudo generar el certificado.',
          variant: 'error'
        });
        return;
      }

      setStudentCertificateUrl(url);

      if (estudianteCursoRow?.idEstudiantesCursos) {
        await axios.put(LMS_ENDPOINTS.estudiantesCurso, {
          idEstudiantesCursos: estudianteCursoRow.idEstudiantesCursos,
          idEstudiante: estudianteCursoRow.idEstudiante,
          idCurso: estudianteCursoRow.idCurso,
          calificacionFinal: estudianteCursoRow.calificacionFinal ?? 0,
          idEstado: estudianteCursoRow.idEstado ?? 0,
          certificadoGenerado: true,
          ultimoAcceso: estudianteCursoRow.ultimoAcceso,
          activo: estudianteCursoRow.activo ?? true,
        });

        setEstudianteCursoRow((prev) => ({
          ...(prev || {}),
          certificadoGenerado: true,
        }));
      }

      const resStudentCerts = await axios.get(LMS_ENDPOINTS.estudianteCertificaciones);
      const existingRows = (resStudentCerts.data || []).map(Models.estudianteCertificaciones);
      const existing = existingRows.find(
        (r) => String(r.idEstudiante) === String(idEstudiante) && String(r.idCurso) === String(courseId)
      );

      if (existing?.idEstudiantesCertificaciones) {
        await axios.put(LMS_ENDPOINTS.estudianteCertificaciones, {
          idEstudiantesCertificaciones: existing.idEstudiantesCertificaciones,
          idEstudiante: Number(idEstudiante) || 0,
          idCertificacion: Number(cert.idCertificacion) || 0,
          fechaAsignacion: new Date().toISOString(),
          rutaCertificadoURL: url,
          idCurso: Number(courseId) || 0,
          activo: true,
        });
      } else {
        await axios.post(LMS_ENDPOINTS.estudianteCertificaciones, {
          idEstudiante: Number(idEstudiante) || 0,
          idCertificacion: Number(cert.idCertificacion) || 0,
          fechaAsignacion: new Date().toISOString(),
          rutaCertificadoURL: url,
          idCurso: Number(courseId) || 0,
          activo: true,
        });
      }

      setAlertDialog({
        open: true,
        title: 'Certificación Generada',
        message: 'Certificación generada y registrada correctamente.',
        variant: 'success'
      });

      // --- GATEWAY IMPROVISADA: Sincronizar todas las carreras del estudiante ---
      try {
        if (idEstudiante) {
          console.log(`[CourseCurriculumStudent] 🔄 Iniciando sincronización de TODAS las carreras para estudiante: ${idEstudiante}...`);
          const resSyncGlobal = await axios.post(LMS_ENDPOINTS.carreraEstudianteSyncTodas(idEstudiante));
          console.log("🔄 ✅ Sincronización global de carreras completada tras terminar curso:", resSyncGlobal.data);
        }
      } catch (syncErr) {
        console.warn("⚠️ ❌ No se pudo sincronizar carreras automáticamente:", syncErr);
      }
      // -------------------------------------------------------------------------

      setCertificateCongratsOpen(false);
    } catch (error) {
      console.error('[CourseCurriculumStudent] Error descargando/generando certificado:', error);
      const serverMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Error desconocido';
      setAlertDialog({
        open: true,
        title: 'Error',
        message: `Ocurrió un error al generar el certificado:\n${serverMsg}`,
        variant: 'error'
      });
    }
  };

  // Cargar todos los intentos de exámenes y cuestionarios
  useEffect(() => {
    const loadAllAttempts = async () => {
      if (!idEstudiante || !course || !course.modulos) return;

      // console.log('[CourseCurriculumStudent] Cargando todos los intentos del estudiante...');
      const attempts = {};

      try {
        const allItems = course.modulos.flatMap(mod => mod.contenido || []);

        // Cargar intentos de exámenes
        const exams = allItems.filter(item => item.tipo === 'EXAMEN');
        for (const exam of exams) {
          try {
            const examId = exam.idExamen || exam.id;
            const intentos = await getExamAttempts(examId, idEstudiante);
            const key = `EXAMEN-${examId}`;
            attempts[key] = intentos.length;
            // console.log(`[CourseCurriculumStudent] ${key}: ${intentos.length} intentos`);
          } catch (error) {
            console.error(`[CourseCurriculumStudent] Error cargando intentos de examen:`, error);
          }
        }

        // Cargar intentos de cuestionarios
        const quizzes = allItems.filter(item => item.tipo === 'CUESTIONARIO');
        for (const quiz of quizzes) {
          try {
            const quizId = quiz.idCuestionario || quiz.id;
            const intentos = await getQuizAttempts(quizId, idEstudiante);
            const key = `CUESTIONARIO-${quizId}`;
            attempts[key] = intentos.length;
            // console.log(`[CourseCurriculumStudent] ${key}: ${intentos.length} intentos`);
          } catch (error) {
            console.error(`[CourseCurriculumStudent] Error cargando intentos de cuestionario:`, error);
          }
        }

        setAllAttempts(attempts);
        // console.log('[CourseCurriculumStudent] Todos los intentos cargados:', attempts);
      } catch (error) {
        console.error('[CourseCurriculumStudent] Error general cargando intentos:', error);
      }
    };

    loadAllAttempts();
  }, [idEstudiante, course]);

  // Cargar progreso del curso
  useEffect(() => {
    const loadProgress = async () => {
      if (idEstudiante && course) {
        // console.log('[CourseCurriculumStudent] Cargando progreso del curso...');

        try {
          // 1. Calcular progreso y promedio (usando el servicio)
          const [progress, avg, quizzesStatus, examsStatus, completadas] = await Promise.all([
            calculateCourseProgress(idEstudiante, course),
            calculateAverageExamGrade(idEstudiante, course),
            getQuizzesStatus(idEstudiante),
            getExamsStatus(idEstudiante),
            getCompletedLessons(idEstudiante)
          ]);

          setCourseProgress(progress);
          setAverageExamGrade(avg);

          // 2. Obtener lecciones completadas IDs
          const idsLeccionesCompletadas = completadas.map(le => le.idLeccion || le.IdLeccion);

          // 3. Combinar todos los IDs aprobados
          const todosAprobados = [
            ...idsLeccionesCompletadas.map((id) => `LECCION-${id}`),
            ...(quizzesStatus.aprobados || []).map((id) => `CUESTIONARIO-${id}`),
            ...(examsStatus.aprobados || []).map((id) => `EXAMEN-${id}`)
          ];
          setCompletedLessonsIds(todosAprobados);

          // 4. Combinar todos los IDs reprobados
          const todosReprobados = [
            ...(quizzesStatus.reprobados || []).map((id) => `CUESTIONARIO-${id}`),
            ...(examsStatus.reprobados || []).map((id) => `EXAMEN-${id}`)
          ];
          setFailedLessonsIds(todosReprobados);

          // console.log('[CourseCurriculumStudent] Progreso cargado:', progress);
        } catch (error) {
          console.error('[CourseCurriculumStudent] Error cargando progreso:', error);
        }
      }
    };
    loadProgress();
  }, [idEstudiante, course]);

  // ---------- Cálculo de stats ----------
  const stats = useMemo(() => {
    if (!course) {
      return {
        totalModules: 0,
        totalLessons: 0,
        totalQuizzes: 0,
        completedLessons: 0,
        durationWeeks: 0,
        estudiantes: 0,
        cuestionarios: 0,
      };
    }
    let totalModules = (course.modulos || []).length;
    let totalLessons = 0;
    let totalQuizzes = 0;
    let totalExams = 0;
    let completedLessons = 0;

    (course.modulos || []).forEach((m) => {
      (m.contenido || []).forEach((item) => {
        if (item.tipo === "LECCION") {
          totalLessons += 1;
          if (item.completado) completedLessons += 1;
        } else if (item.tipo === "CUESTIONARIO") {
          totalQuizzes += 1;
        } else if (item.tipo === "EXAMEN") {
          totalExams += 1;
        }
      });
    });

    return {
      totalModules,
      totalLessons,
      totalQuizzes,
      totalExams,
      completedLessons,
      durationWeeks: course.duracionSemanas || 0,
      estudiantes: course.estudiantes || 0,
      cuestionarios:
        (course.cuestionariosDetalle || []).length || 0,
    };
  }, [course]);

  const allExpanded = useMemo(() => {
    if (!course || !course.modulos || course.modulos.length === 0)
      return true;
    return course.modulos.every((m) => expandedModules[m.id]);
  }, [course, expandedModules]);

  const getItemProgressKey = (item) => {
    const itemId = item.idLeccion || item.idCuestionario || item.idExamen || item.id;
    return `${item.tipo}-${itemId}`;
  };

  const isItemCompletedForUnlock = (item) => {
    const progressKey = getItemProgressKey(item);
    const isApproved = completedLessonsIds.includes(progressKey);
    if (isApproved) return true;

    // Para CUESTIONARIOS: No bloquean el flujo, pero requieren al menos 1 intento para continuar
    if (item.tipo === 'CUESTIONARIO') {
      const id = item.idCuestionario || item.id;
      const attemptsKey = `${item.tipo}-${id}`;
      const intentosRealizados = allAttempts[attemptsKey] || 0;
      return intentosRealizados >= 1;
    }

    // Para EXAMENES: Desbloquea solo si está aprobado O si agotó intentos
    if (item.tipo === 'EXAMEN') {
      const isFailed = failedLessonsIds.includes(progressKey);
      if (!isFailed) return false;

      const id = item.idExamen || item.id;
      const attemptsKey = `${item.tipo}-${id}`;
      const intentosRealizados = allAttempts[attemptsKey] || 0;
      const limiteIntentos = item.limiteIntentos ?? 3;

      if (limiteIntentos === 0) return false;
      return intentosRealizados >= limiteIntentos;
    }

    return false;
  };

  const isItemUnlocked = (moduleIndex, itemIndex) => {
    if (!course || !course.modulos) return true;
    const modulos = course.modulos || [];
    const modulo = modulos[moduleIndex];
    if (!modulo) return true;
    const contenido = modulo.contenido || [];

    // Si es el primer item del primer módulo, siempre desbloqueado
    if (moduleIndex === 0 && itemIndex === 0) return true;

    // Si hay un item anterior en el mismo módulo, depende de que ese item esté aprobado
    if (itemIndex > 0) {
      const prevItem = contenido[itemIndex - 1];
      if (!prevItem) return true;
      return isItemCompletedForUnlock(prevItem);
    }

    // Si es el primer item del módulo (itemIndex === 0), depende de que el módulo anterior esté completamente aprobado
    if (moduleIndex > 0) {
      const prevModule = modulos[moduleIndex - 1];
      const prevContenido = prevModule?.contenido || [];
      if (prevContenido.length === 0) return true;
      return prevContenido.every((it) => isItemCompletedForUnlock(it));
    }

    return true;
  };

  const isCourseFailedDefinitively = useMemo(() => {
    // 1. Debe haber terminado todas las actividades (porcentaje === 100)
    if (courseProgress.porcentaje < 100) return false;

    // 2. Debe tener al menos 1 examen y el promedio debe ser menor a 60
    if (averageExamGrade.totalExamenes > 0 && averageExamGrade.promedio < 60) {
      return true;
    }

    return false;
  }, [courseProgress.porcentaje, averageExamGrade]);

  const handleReiniciarProgreso = async () => {
    setAlertDialog({
      open: true,
      title: 'Confirmar Reinicio',
      message: '¿Estás seguro de que deseas recursar este curso? Se borrará todo tu progreso actual y los intentos realizados.',
      variant: 'warning',
      cancelText: 'Cancelar',
      buttonText: 'Sí, Reiniciar',
      onCancel: () => setAlertDialog({ open: false }),
      onConfirm: async () => {
        try {
          const response = await axios.post(LMS_ENDPOINTS.estudianteCursoReiniciarProgreso(Number(slug), idEstudiante));
          if (response.status === 200) {
            setAlertDialog({
              open: true,
              title: 'Curso Reiniciado',
              message: 'Tu progreso ha sido eliminado. Ahora puedes comenzar el curso desde cero.',
              variant: 'success'
            });
            // Recargar datos
            loadCursoCompleto();
          }
        } catch (error) {
          console.error("Error al reiniciar progreso:", error);
          setAlertDialog({
            open: true,
            title: 'Error',
            message: 'No se pudo reiniciar el progreso del curso. Por favor, intenta más tarde.',
            variant: 'error'
          });
        }
      }
    });
  };

  // ---------- Persistencia helper ----------
  const persistCourse = async (updatedCourse) => {
    if (!updatedCourse || !updatedCourse.idCurso) {
      console.error("No se puede guardar: curso sin ID");
      return;
    }

    setCourse(updatedCourse);

    try {
      const cursoDTO = {
        IdCurso: updatedCourse.idCurso,
        IdInstructor: updatedCourse.idInstructor || 0,
        Nombre: updatedCourse.titulo || updatedCourse.nombre || "",
        IdNivel: updatedCourse.idNivel || 0,
        Duracion: updatedCourse.duracion || 0,
        IdContenido: updatedCourse.idContenido || 0,
        URLRutaImagen: updatedCourse.uRLRutaImagen || "",
        Activo:
          updatedCourse.activo !== undefined
            ? updatedCourse.activo
            : true,
      };

      await axios.put(LMS_ENDPOINTS.cursos, cursoDTO);
      console.log("✅ Curso actualizado exitosamente en BD");
    } catch (error) {
      console.error("❌ Error al guardar cambios del curso:", error);
      setAlertDialog({
        open: true,
        title: 'Error de Guardado',
        message: "No se pudieron guardar los cambios en la base de datos.",
        variant: 'error'
      });
    }
  };

  // ---------- Persistencia helper para módulos ----------
  const persistModule = async (updatedModule) => {
    if (!updatedModule || !updatedModule.idModulo) {
      console.error("No se puede guardar: módulo sin ID");
      return false;
    }

    try {
      const moduloDTO = {
        IdModulo: updatedModule.idModulo,
        IdCurso: updatedModule.idCurso,
        Nombre: updatedModule.titulo || updatedModule.nombre || "",
        Orden: updatedModule.orden || 0,
        Activo:
          updatedModule.activo !== undefined
            ? updatedModule.activo
            : true,
      };

      const response = await axios.put(LMS_ENDPOINTS.modulo, moduloDTO);

      if (response.data) {
        console.log("✅ Módulo actualizado exitosamente en BD");
        return true;
      }
      return false;
    } catch (error) {
      console.error("❌ Error al guardar cambios del módulo:", error);
      setAlertDialog({
        open: true,
        title: 'Error de Guardado',
        message: "No se pudieron guardar los cambios del módulo en la base de datos.",
        variant: 'error'
      });
      return false;
    }
  };

  // ---------- Persistencia helper para lecciones ----------
  const persistLesson = async (updatedLesson) => {
    if (!updatedLesson || !updatedLesson.idLeccion) {
      console.error("No se puede guardar: lección sin ID");
      return false;
    }

    try {
      const leccionDTO = {
        IdLeccion: updatedLesson.idLeccion,
        IdModulo: updatedLesson.idModulo,
        Titulo: updatedLesson.titulo || "",
        Monedas: updatedLesson.monedas || 0,
        Activo:
          updatedLesson.activo !== undefined
            ? updatedLesson.activo
            : true,
      };

      const response = await axios.put(
        LMS_ENDPOINTS.lecciones,
        leccionDTO
      );

      if (response.data) {
        console.log("✅ Lección actualizada exitosamente en BD");
        return true;
      }
      return false;
    } catch (error) {
      console.error("❌ Error al guardar cambios de la lección:", error);
      setAlertDialog({
        open: true,
        title: 'Error de Guardado',
        message: "No se pudieron guardar los cambios de la lección en la base de datos.",
        variant: 'error'
      });
      return false;
    }
  };

  // ---------- Edición: título de cuestionario ----------
  const startEditQuizTitle = (moduleId, quizId, currentTitle) => {
    // Disabled for student
  };

  const saveQuizTitle = async () => {
    if (!editingQuiz || !course) return;

    const trimmed = quizTitleDraft.trim();
    if (!trimmed) {
      setAlertDialog({
        open: true,
        title: 'Campo Requerido',
        message: "El título del cuestionario no puede estar vacío.",
        variant: 'warning'
      });
      return;
    }

    const modulo = course.modulos.find(
      (m) => m.id === editingQuiz.moduleId
    );
    const quiz = modulo?.contenido?.find(
      (c) => c.tipo === "CUESTIONARIO" && c.id === editingQuiz.quizId
    );

    if (!quiz) {
      setEditingQuiz(null);
      return;
    }

    if (quiz.titulo === trimmed) {
      setEditingQuiz(null);
      return;
    }

    // Actualizar estado local primero para ver el cambio inmediatamente
    const updated = {
      ...course,
      modulos: course.modulos.map((m) => {
        if (m.id !== editingQuiz.moduleId) return m;
        return {
          ...m,
          contenido: m.contenido.map((c) =>
            c.tipo === "CUESTIONARIO" && c.id === editingQuiz.quizId
              ? { ...c, titulo: trimmed }
              : c
          ),
        };
      }),
    };
    setCourse(updated);
    setEditingQuiz(null);

    // Luego guardar en BD
    try {
      const quizDTO = {
        idCuestionario: quiz.idCuestionario,
        idCurso: course.idCurso,
        nombre: trimmed,
        activo: quiz.activo !== undefined ? quiz.activo : true,
      };

      await axios.put(
        LMS_ENDPOINTS.cuestionario,
        quizDTO
      );

      console.log("✅ Título del cuestionario actualizado en BD");
    } catch (error) {
      console.error("❌ Error al actualizar título del cuestionario:", error);
      setAlertDialog({
        open: true,
        title: 'Error de Actualización',
        message: "No se pudieron guardar los cambios del cuestionario en la base de datos.",
        variant: 'error'
      });
      // Recargar en caso de error
      await loadCursoCompleto();
    }
  };

  const handleQuizTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveQuizTitle();
    } else if (e.key === "Escape") {
      setEditingQuiz(null);
    }
  };

  // ---------- Handlers generales ----------
  const handleToggleModule = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  const handleToggleAllModules = () => {
    if (!course) return;
    const nextValue = !allExpanded;
    const next = {};
    (course.modulos || []).forEach((m) => {
      next[m.id] = nextValue;
    });
    setExpandedModules(next);
  };

  const handleToggleLessonCompleted = (moduleId, lessonId) => {
    // Disabled for student: completion is automatic via player
    console.log("Toggle completion disabled for student view");
  };

  // ---------- Agregar lección ----------
  const handleAddLesson = (moduleId) => {
    if (!course) return;
    const updated = { ...course };

    const modIndex = updated.modulos.findIndex(
      (m) => m.id === moduleId
    );
    if (modIndex === -1) return;

    const targetModule = updated.modulos[modIndex];
    const currentCount = (targetModule.lecciones || []).length;
    const newLessonId = Date.now();

    const nuevaLeccion = {
      id: newLessonId,
      titulo: `Nueva lección ${currentCount + 1}`,
      duracionMin: 20,
      completado: false,
    };

    updated.modulos[modIndex] = {
      ...targetModule,
      lecciones: [...(targetModule.lecciones || []), nuevaLeccion],
    };

    persistCourse(updated);
  };

  // ---------- Eliminar lección ----------
  const handleDeleteLesson = (moduleId, lessonId) => {
    // Disabled for student
  };

  const confirmDeleteLesson = async () => {
    if (
      !course ||
      !deleteLessonDialog.moduleId ||
      !deleteLessonDialog.lessonId
    )
      return;

    try {
      await axios.delete(
        LMS_ENDPOINTS.leccionesById(deleteLessonDialog.lessonId)
      );

      console.log("✅ Lección eliminada exitosamente de BD");

      const updated = {
        ...course,
        modulos: course.modulos.map((m) => {
          if (m.id !== deleteLessonDialog.moduleId) return m;
          return {
            ...m,
            lecciones: (m.lecciones || []).filter(
              (l) => l.id !== deleteLessonDialog.lessonId
            ),
          };
        }),
      };
      setCourse(updated);
    } catch (error) {
      console.error("❌ Error al eliminar lección:", error);
      setAlertDialog({
        open: true,
        title: 'Error de Eliminación',
        message: "No se pudo eliminar la lección de la base de datos.",
        variant: 'error'
      });
    }

    setDeleteLessonDialog({
      open: false,
      moduleId: null,
      lessonId: null,
      lessonName: "",
    });
  };

  // ---------- Eliminar cuestionario ----------
  const handleDeleteQuiz = (moduleId, quizId) => {
    // Disabled for student
  };

  const confirmDeleteQuiz = async () => {
    if (
      !course ||
      !deleteQuizDialog.moduleId ||
      !deleteQuizDialog.quizId
    )
      return;

    try {
      // 1. Eliminar el cuestionario
      await axios.delete(
        LMS_ENDPOINTS.cuestionarioById(deleteQuizDialog.quizId)
      );

      console.log("✅ Cuestionario eliminado exitosamente de BD");

      // 2. Eliminar el registro de ModuloContenido
      // Primero necesitamos encontrar el idModuloContenido
      const respModuloContenido = await axios.get(LMS_ENDPOINTS.moduloContenido);
      const todoModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);

      const registroContenido = todoModuloContenido.find(
        (mc) => mc.idModulo === deleteQuizDialog.moduleId &&
          mc.idAsociado === deleteQuizDialog.quizId &&
          mc.tablaContenido === "Cuestionario"
      );

      if (registroContenido) {
        await axios.delete(
          LMS_ENDPOINTS.moduloContenidoById(registroContenido.idModuloContenido)
        );
        console.log("✅ Registro de ModuloContenido eliminado");
      }

      // 3. Recargar el curso
      await loadCursoCompleto();
    } catch (error) {
      console.error("❌ Error al eliminar cuestionario:", error);
      setAlertDialog({
        open: true,
        title: 'Error de Eliminación',
        message: "No se pudo eliminar el cuestionario de la base de datos.",
        variant: 'error'
      });
    }

    setDeleteQuizDialog({
      open: false,
      moduleId: null,
      quizId: null,
      quizName: "",
    });
  };

  // ---------- Edición: título de examen ----------

  const startEditExamTitle = (moduleId, examId, currentTitle) => {
    // Disabled for student
  };

  const saveExamTitle = async () => {
    if (!editingExam || !course) return;

    const trimmed = examTitleDraft.trim();
    if (!trimmed) {
      setAlertDialog({
        open: true,
        title: 'Campo Requerido',
        message: "El título del examen no puede estar vacío.",
        variant: 'warning'
      });
      return;
    }

    const modulo = course.modulos.find(
      (m) => m.id === editingExam.moduleId
    );
    const exam = modulo?.contenido?.find(
      (c) => c.tipo === "EXAMEN" && c.id === editingExam.examId
    );

    if (!exam) {
      setEditingExam(null);
      return;
    }

    if (exam.titulo === trimmed) {
      setEditingExam(null);
      return;
    }

    // Actualizar estado local primero para ver el cambio inmediatamente
    const updated = {
      ...course,
      modulos: course.modulos.map((m) => {
        if (m.id !== editingExam.moduleId) return m;
        return {
          ...m,
          contenido: m.contenido.map((c) =>
            c.tipo === "EXAMEN" && c.id === editingExam.examId
              ? { ...c, titulo: trimmed }
              : c
          ),
        };
      }),
    };
    setCourse(updated);
    setEditingExam(null);

    // Luego guardar en BD
    try {
      const examDTO = {
        idExamen: exam.idExamen,
        idCurso: course.idCurso,
        descripcion: exam.descripcion || "descripcion",
        nombre: trimmed,
        limiteIntentos: exam.limiteIntentos ?? 3,
        activo: exam.activo !== undefined ? exam.activo : true,
      };

      await axios.put(
        LMS_ENDPOINTS.examen,
        examDTO
      );

      console.log("✅ Título del examen actualizado en BD");
    } catch (error) {
      console.error("❌ Error al actualizar título del examen:", error);
      setAlertDialog({
        open: true,
        title: 'Error de Actualización',
        message: "No se pudieron guardar los cambios del examen en la base de datos.",
        variant: 'error'
      });
      // Recargar en caso de error
      await loadCursoCompleto();
    }
  };

  const handleExamTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveExamTitle();
    } else if (e.key === "Escape") {
      setEditingExam(null);
    }
  };

  // ---------- Eliminar examen ----------
  const handleDeleteExam = (moduleId, examId) => {
    // Disabled for student
  };

  const confirmDeleteExam = async () => {
    if (
      !course ||
      !deleteExamDialog.moduleId ||
      !deleteExamDialog.examId
    )
      return;

    try {
      // 1. Eliminar el examen
      await axios.delete(
        LMS_ENDPOINTS.examenById(deleteExamDialog.examId)
      );

      console.log("✅ Examen eliminado exitosamente de BD");

      // 2. Eliminar el registro de ModuloContenido
      const respModuloContenido = await axios.get(LMS_ENDPOINTS.moduloContenido);
      const todoModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);

      const registroContenido = todoModuloContenido.find(
        (mc) => mc.idModulo === deleteExamDialog.moduleId &&
          mc.idAsociado === deleteExamDialog.examId &&
          mc.tablaContenido === "Examen"
      );

      if (registroContenido) {
        await axios.delete(
          LMS_ENDPOINTS.moduloContenidoById(registroContenido.idModuloContenido)
        );
        console.log("✅ Registro de ModuloContenido eliminado");
      }

      // 3. Recargar el curso
      await loadCursoCompleto();
    } catch (error) {
      console.error("❌ Error al eliminar examen:", error);
      setAlertDialog({
        open: true,
        title: 'Error de Eliminación',
        message: "No se pudo eliminar el examen de la base de datos.",
        variant: 'error'
      });
    }

    setDeleteExamDialog({
      open: false,
      moduleId: null,
      examId: null,
      examName: "",
    });
  };

  // ---------- Drag & Drop para reordenar contenido ----------
  const handleDragStart = (e) => {
    // Disabled for student
    e.preventDefault();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    // Drag and drop saving disabled for students
  };

  // ---------- Agregar módulo ----------
  const handleAddModule = async () => {
    // Disabled for student
  };

  // ---------- Eliminar módulo ----------
  const handleDeleteModule = (moduleId) => {
    if (!course) return;
    const modulo = course.modulos.find((m) => m.id === moduleId);
    const nombre = modulo?.titulo || "este módulo";

    setDeleteModuleDialog({
      open: true,
      moduleId,
      moduleName: nombre,
    });
  };

  const confirmDeleteModule = async () => {
    if (!course || !deleteModuleDialog.moduleId) return;

    try {
      await axios.delete(
        LMS_ENDPOINTS.moduloById(deleteModuleDialog.moduleId)
      );

      console.log("✅ Módulo eliminado exitosamente de BD");

      const updated = {
        ...course,
        modulos: course.modulos.filter(
          (m) => m.id !== deleteModuleDialog.moduleId
        ),
      };
      setCourse(updated);
    } catch (error) {
      console.error("❌ Error al eliminar módulo:", error);
      setAlertDialog({
        open: true,
        title: 'Error de Eliminación',
        message: "No se pudo eliminar el módulo de la base de datos.",
        variant: 'error'
      });
    }

    setDeleteModuleDialog({
      open: false,
      moduleId: null,
      moduleName: "",
    });
  };

  // ---------- Modal NUEVO (Lección / Cuestionario / Evaluación) ----------
  const openNewContentModal = (moduleId) => {
    setNewContentModal({ open: true, moduleId });
  };

  const closeNewContentModal = () => {
    setNewContentModal({ open: false, moduleId: null });
  };

  const handleCreateContent = async (kind) => {
    if (!course || !newContentModal.moduleId) return;

    const moduleId = newContentModal.moduleId;
    const modulo = course.modulos.find(
      (m) => m.idModulo === moduleId
    );

    if (!modulo) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: "Módulo no encontrado",
        variant: 'error'
      });
      return;
    }

    try {
      if (kind === "LECCION") {
        const existing = modulo.contenido || [];
        const countLessons = existing.filter(
          (x) => x.tipo === "LECCION"
        ).length;

        // Calcular el siguiente orden
        const maxOrden = existing.length > 0
          ? Math.max(...existing.map(c => c.idModuloContenido || 0))
          : 0;
        const nuevoOrden = maxOrden + 1;

        // 1. Crear lección
        const leccionDTO = {
          IdModulo: moduleId,
          Titulo: `Nueva lección ${countLessons + 1}`,
          Monedas: 0,
          Activo: true,
        };

        const response = await axios.post(
          LMS_ENDPOINTS.lecciones,
          leccionDTO
        );

        if (!response.data) {
          setAlertDialog({
            open: true,
            title: 'Error',
            message: "No se pudo crear la lección en la base de datos",
            variant: 'error'
          });
          return;
        }

        const nuevaLeccionId = response.data;
        console.log(
          `✅ Lección creada en BD con ID: ${nuevaLeccionId}`
        );

        // 2. Crear registro en ModuloContenido con orden
        const moduloContenidoDTO = {
          IdModuloContenido: 0,
          IdModulo: moduleId,
          IdAsociado: nuevaLeccionId,
          TablaContenido: "Lecciones",
          Activo: true,
          Orden: nuevoOrden
        };

        await axios.post(LMS_ENDPOINTS.moduloContenido, moduloContenidoDTO);
        console.log(`✅ Registro en ModuloContenido creado con orden: ${nuevoOrden}`);

        // 3. Recargar curso
        await loadCursoCompleto();
        closeNewContentModal();

      } else if (kind === "CUESTIONARIO") {
        const existing = modulo.contenido || [];
        const countQuizzes = existing.filter(
          (x) => x.tipo === "CUESTIONARIO"
        ).length;

        // Calcular el siguiente orden
        const maxOrden = existing.length > 0
          ? Math.max(...existing.map(c => c.idModuloContenido || 0))
          : 0;
        const nuevoOrden = maxOrden + 1;

        const nombreBase = `Cuestionario ${countQuizzes + 1}`;

        // 1. Crear cuestionario
        const cuestionarioDTO = {
          idCurso: course.idCurso,
          nombre:
            course.titulo && course.titulo.length > 0
              ? `${course.titulo} · ${nombreBase}`
              : nombreBase,
        };

        console.log("📝 Creando cuestionario:", cuestionarioDTO);

        const response = await axios.post(
          LMS_ENDPOINTS.cuestionario,
          cuestionarioDTO
        );

        console.log("📦 Respuesta del servidor:", response.data);

        if (!response.data) {
          setAlertDialog({
            open: true,
            title: 'Error',
            message: "No se pudo crear el cuestionario en la base de datos",
            variant: 'error'
          });
          return;
        }

        const nuevoCuestionarioId = response.data;
        console.log(
          `✅ Cuestionario creado en BD con ID: ${nuevoCuestionarioId}`
        );

        // 2. Crear registro en ModuloContenido con orden
        const moduloContenidoDTO = {
          IdModuloContenido: 0,
          IdModulo: moduleId,
          IdAsociado: nuevoCuestionarioId,
          TablaContenido: "Cuestionario",
          Activo: true,
          Orden: nuevoOrden
        };

        await axios.post(LMS_ENDPOINTS.moduloContenido, moduloContenidoDTO);
        console.log(`✅ Registro en ModuloContenido creado con orden: ${nuevoOrden}`);

        closeNewContentModal();
        navigate(
          `/menu/lms/cuestionario/${nuevoCuestionarioId}/preguntas`
        );

      } else if (kind === "EVALUACION") {
        const existing = modulo.contenido || [];
        const countExams = existing.filter(
          (x) => x.tipo === "EXAMEN"
        ).length;

        // Calcular el siguiente orden
        const maxOrden = existing.length > 0
          ? Math.max(...existing.map(c => c.idModuloContenido || 0))
          : 0;
        const nuevoOrden = maxOrden + 1;

        const nombreBase = `Evaluación ${countExams + 1}`;

        // 1. Crear examen
        const examenDTO = {
          idExamen: 0,
          idCurso: course.idCurso,
          descripcion: "descripcion",
          nombre:
            course.titulo && course.titulo.length > 0
              ? `${course.titulo} · ${nombreBase}`
              : nombreBase,
          limiteIntentos: 3,
          activo: true
        };

        console.log("📝 Creando examen:", examenDTO);

        const response = await axios.post(
          LMS_ENDPOINTS.examen,
          examenDTO
        );

        console.log("📦 Respuesta del servidor:", response.data);

        if (!response.data) {
          setAlertDialog({
            open: true,
            title: 'Error',
            message: "No se pudo crear el examen en la base de datos",
            variant: 'error'
          });
          return;
        }

        const nuevoExamenId = response.data;
        console.log(
          `✅ Examen creado en BD con ID: ${nuevoExamenId}`
        );

        // 2. Crear registro en ModuloContenido con orden
        const moduloContenidoExamenDTO = {
          IdModuloContenido: 0,
          IdModulo: moduleId,
          IdAsociado: nuevoExamenId,
          TablaContenido: "Examen",
          Activo: true,
          Orden: nuevoOrden
        };

        await axios.post(LMS_ENDPOINTS.moduloContenido, moduloContenidoExamenDTO);
        console.log(`✅ Registro en ModuloContenido creado con orden: ${nuevoOrden}`);

        closeNewContentModal();
        navigate(
          `/menu/lms/examen/${nuevoExamenId}/preguntas`
        );
      }
    } catch (error) {
      console.error(`❌ Error al crear ${kind}:`, error);
      setAlertDialog({
        open: true,
        title: 'Error de Creación',
        message: `No se pudo crear el contenido en la base de datos.`,
        variant: 'error'
      });
    }
  };

  // ---------- Edición: título del curso ----------
  const startEditCourseTitle = () => {
    // Disabled for student
  };

  const cancelEditCourseTitle = () => {
    setEditingCourseTitle(false);
    setCourseTitleDraft("");
  };

  const saveCourseTitle = () => {
    if (!course) return;
    const text = courseTitleDraft.trim();
    if (!text) {
      cancelEditCourseTitle();
      return;
    }
    const updated = { ...course, titulo: text, nombre: text };
    persistCourse(updated);
    setEditingCourseTitle(false);
  };

  const handleCourseTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveCourseTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditCourseTitle();
    }
  };

  // ---------- Edición: título del módulo ----------
  const startEditModuleTitle = (moduleId, currentTitle) => {
    // Disabled for student
  };

  const cancelEditModuleTitle = () => {
    setEditingModuleId(null);
    setModuleTitleDraft("");
  };

  const saveModuleTitle = async () => {
    if (!course || editingModuleId == null) {
      cancelEditModuleTitle();
      return;
    }
    const text = moduleTitleDraft.trim();
    if (!text) {
      cancelEditModuleTitle();
      return;
    }

    const moduloAActualizar = course.modulos.find(
      (m) => m.id === editingModuleId
    );
    if (!moduloAActualizar) {
      cancelEditModuleTitle();
      return;
    }

    const updated = {
      ...course,
      modulos: course.modulos.map((m) =>
        m.id === editingModuleId
          ? { ...m, titulo: text, nombre: text }
          : m
      ),
    };
    setCourse(updated);
    setEditingModuleId(null);

    await persistModule({
      ...moduloAActualizar,
      titulo: text,
      nombre: text,
    });
  };

  const handleModuleTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveModuleTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditModuleTitle();
    }
  };

  // ---------- Edición: título de la lección ----------
  const startEditLessonTitle = (
    moduleId,
    lessonId,
    currentTitle
  ) => {
    // Disabled for student
  };

  const cancelEditLessonTitle = () => {
    setEditingLesson(null);
    setLessonTitleDraft("");
  };

  const saveLessonTitle = async () => {
    if (!course || !editingLesson) {
      cancelEditLessonTitle();
      return;
    }
    const text = lessonTitleDraft.trim();
    if (!text) {
      cancelEditLessonTitle();
      return;
    }

    const { moduleId, lessonId } = editingLesson;

    const modulo = course.modulos.find((m) => m.id === moduleId);
    const leccionAActualizar = modulo?.contenido?.find(
      (c) => c.tipo === "LECCION" && c.id === lessonId
    );

    if (!leccionAActualizar) {
      cancelEditLessonTitle();
      return;
    }

    // Actualizar estado local primero para ver el cambio inmediatamente
    const updated = {
      ...course,
      modulos: course.modulos.map((m) => {
        if (m.id !== moduleId) return m;
        return {
          ...m,
          contenido: m.contenido.map((c) =>
            c.tipo === "LECCION" && c.id === lessonId
              ? { ...c, titulo: text }
              : c
          ),
        };
      }),
    };
    setCourse(updated);
    setEditingLesson(null);

    // Luego guardar en BD
    await persistLesson({
      ...leccionAActualizar,
      titulo: text,
    });
  };

  const handleLessonTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveLessonTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditLessonTitle();
    }
  };

  // Función helper para verificar si se puede mostrar el botón "Ver"
  const canShowViewButton = (item) => {
    // Si es un EXAMEN y ya se agotaron los intentos sin haber aprobado,
    // bloqueamos el acceso ("Ver") para que no pueda entrar al player.
    if (item.tipo === 'EXAMEN') {
      const progressKey = getItemProgressKey(item);
      const isApproved = completedLessonsIds.includes(progressKey);
      const intentos = allAttempts[progressKey] || 0;
      const limite = item.limiteIntentos ?? 3;

      if (!isApproved && limite > 0 && intentos >= limite) {
        return false;
      }
    }
    return true;
  };

  const handleContinueCourse = () => {
    if (!course || !course.modulos) return;

    for (let moduleIndex = 0; moduleIndex < course.modulos.length; moduleIndex += 1) {
      const modulo = course.modulos[moduleIndex];
      const contenido = modulo?.contenido || [];

      for (let itemIndex = 0; itemIndex < contenido.length; itemIndex += 1) {
        const item = contenido[itemIndex];
        if (!item) continue;

        if (!isItemUnlocked(moduleIndex, itemIndex)) continue;
        if (!canShowViewButton(item)) continue;

        const progressKey = getItemProgressKey(item);
        const alreadyCompleted = completedLessonsIds.includes(progressKey);
        if (alreadyCompleted) continue;

        if (item.tipo === 'EXAMEN' && certificadoGenerado) continue;

        if (item.tipo === 'LECCION') {
          const lessonId = item.idLeccion || item.id;
          navigate(`/menu/lms/curso-estudiante/${slug}/player/${lessonId}`);
          return;
        }

        if (item.tipo === 'CUESTIONARIO') {
          const quizId = item.idCuestionario || item.id;
          navigate(`/menu/lms/curso-estudiante/${slug}/player/${quizId}?type=quiz`);
          return;
        }

        if (item.tipo === 'EXAMEN') {
          const examId = item.idExamen || item.id;
          navigate(`/menu/lms/curso-estudiante/${slug}/player/${examId}?type=exam`);
          return;
        }
      }
    }

    setAlertDialog({
      open: true,
      title: 'Información',
      message: 'No se encontró contenido disponible para continuar.',
      variant: 'info'
    });
  };


  // ========= RENDER =========
  if (loading) {
    return (
      <div className="cc-page">
        <p>Cargando curso...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cc-page">
        <p style={{ color: "red" }}>
          Error al cargar el curso.
        </p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="cc-page">
        <p>No se encontró el curso.</p>
      </div>
    );
  }

  return (
    <div className="cc-page">
      {/* Breadcrumb */}
      <div className="cc-breadcrumb">
        <button
          type="button"
          className="cc-breadcrumb-back"
          onClick={() => navigate('/menu/lms/mis-cursos')}
        >
          <FiArrowLeft />
          Volver a Mis Cursos
        </button>
      </div>

      <div className="cc-layout">
        {/* MAIN */}
        <div className="cc-main">
          {/* Título de curso editable */}
          {editingCourseTitle ? (
            <input
              className="cc-inline-input cc-inline-input-course"
              value={courseTitleDraft}
              onChange={(e) =>
                setCourseTitleDraft(e.target.value)
              }
              onBlur={saveCourseTitle}
              onKeyDown={handleCourseTitleKeyDown}
              autoFocus
            />
          ) : (
            <h1
              className="cc-course-title"
              onDoubleClick={startEditCourseTitle}
              title="Doble clic para editar el título del curso"
            >
              {course.titulo}
            </h1>
          )}

          {/* Chips resumen */}
          <div className="cc-chip-row">
            <span className="cc-chip">
              {stats.totalModules} módulos
            </span>
            <span className="cc-chip">
              {stats.totalLessons} lecciones
            </span>
            <span className="cc-chip">
              {stats.totalQuizzes} cuestionarios
            </span>
            <span className="cc-chip">
              {stats.totalExams} evaluaciones
            </span>
          </div>

          {/* Toolbar */}
          <div className="cc-toolbar">
            <button
              type="button"
              className="cc-add-lesson-btn"
              onClick={handleToggleAllModules}
            >
              {allExpanded
                ? "Contraer todas las secciones"
                : "Expandir todas las secciones"}
            </button>
          </div>

          {/* Lista de módulos */}
          <div className="cc-modules-list">
            {(course.modulos || []).map((modulo, index) => {
              const isExpanded = expandedModules[modulo.id];
              const contentCount = (modulo.contenido || []).length;

              const isEditingThisModule =
                editingModuleId === modulo.id;

              return (
                <div
                  key={modulo.id}
                  className="cc-module-card"
                >
                  {/* Header módulo */}
                  <div className="cc-module-header">
                    <div className="cc-module-header-left">
                      <div className="cc-module-index-badge">
                        {index + 1}
                      </div>
                      <div>
                        <div
                          className="cc-module-title-wrapper"
                          onDoubleClick={() =>
                            startEditModuleTitle(
                              modulo.id,
                              modulo.titulo
                            )
                          }
                        >
                          {isEditingThisModule ? (
                            <input
                              className="cc-inline-input"
                              value={moduleTitleDraft}
                              onChange={(e) =>
                                setModuleTitleDraft(
                                  e.target.value
                                )
                              }
                              onBlur={saveModuleTitle}
                              onKeyDown={
                                handleModuleTitleKeyDown
                              }
                              autoFocus
                            />
                          ) : (
                            <h2
                              className="cc-module-title"
                              title="Doble clic para editar"
                            >
                              {modulo.titulo}
                            </h2>
                          )}
                        </div>
                        <p className="cc-module-meta">
                          {contentCount}{" "}
                          {contentCount === 1
                            ? "elemento"
                            : "elementos"}
                        </p>
                      </div>
                    </div>

                    <div className="cc-module-header-right">
                      <button
                        type="button"
                        className="cc-module-toggle-btn"
                        onClick={() =>
                          handleToggleModule(modulo.id)
                        }
                        title={
                          isExpanded
                            ? "Contraer"
                            : "Expandir"
                        }
                      >
                        {isExpanded ? (
                          <FiChevronUp />
                        ) : (
                          <FiChevronDown />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Contenido del módulo (si expandido) */}
                  {isExpanded && (
                    <div className="cc-lessons-list">
                      {(modulo.contenido || []).length === 0 && (
                        <p className="cc-empty-lessons">
                          No hay contenido disponible.
                        </p>
                      )}

                      {(modulo.contenido || []).map((item, itemIndex) => {
                        const unlocked = isItemUnlocked(index, itemIndex);

                        const isEditingThisItem =
                          (editingLesson?.moduleId === modulo.id &&
                            editingLesson?.lessonId === item.id) ||
                          (editingQuiz?.moduleId === modulo.id &&
                            editingQuiz?.quizId === item.id) ||
                          (editingExam?.moduleId === modulo.id &&
                            editingExam?.examId === item.id);

                        // Determinar icono según tipo
                        const Icon = item.tipo === "LECCION"
                          ? FiFileText
                          : item.tipo === "CUESTIONARIO"
                            ? FiHelpCircle
                            : FiClipboard;

                        const isDragging = draggedItem?.item?.id === item.id;
                        const isDragOver = dragOverIndex === itemIndex;

                        return (
                          <div
                            key={`${item.tipo}-${item.id}`}
                            className={`cc-lesson-row ${(() => {
                              const itemId = item.idLeccion || item.idCuestionario || item.idExamen || item.id;
                              const progressKey = `${item.tipo}-${itemId}`;
                              const isCompleted = completedLessonsIds.includes(progressKey);
                              const isFailed = failedLessonsIds.includes(progressKey);

                              if (isCompleted) return "cc-lesson-completed";
                              if (isFailed) return "cc-lesson-failed";
                              return "";
                            })()} ${item.tipo === "CUESTIONARIO" ? "cc-quiz-row" : ""}`}
                          >
                            {/* Main */}
                            <div
                              className="cc-lesson-main"
                              onClick={() =>
                                unlocked && item.tipo === "LECCION" && handleToggleLessonCompleted(
                                  modulo.id,
                                  item.id
                                )
                              }
                            >
                              <div className="cc-lesson-icon">
                                {(() => {
                                  const itemId = item.idLeccion || item.idCuestionario || item.idExamen || item.id;
                                  const progressKey = `${item.tipo}-${itemId}`;
                                  const isCompleted = completedLessonsIds.includes(progressKey);
                                  const isFailed = failedLessonsIds.includes(progressKey);

                                  if (isCompleted) {
                                    return <FiCheckCircle style={{ color: '#16a34a' }} />; // Verde
                                  } else if (isFailed) {
                                    return <FiXCircle style={{ color: '#dc2626' }} />; // Rojo
                                  } else {
                                    return <Icon />; // Normal
                                  }
                                })()}
                              </div>
                              <div className="cc-lesson-texts">
                                {isEditingThisItem && item.tipo === "LECCION" ? (
                                  <input
                                    className="cc-inline-input"
                                    value={
                                      lessonTitleDraft
                                    }
                                    onChange={(e) =>
                                      setLessonTitleDraft(
                                        e.target.value
                                      )
                                    }
                                    onBlur={
                                      saveLessonTitle
                                    }
                                    onKeyDown={
                                      handleLessonTitleKeyDown
                                    }
                                    autoFocus
                                    onClick={(e) =>
                                      e.stopPropagation()
                                    }
                                  />
                                ) : isEditingThisItem && item.tipo === "CUESTIONARIO" ? (
                                  <input
                                    className="cc-inline-input"
                                    value={
                                      quizTitleDraft
                                    }
                                    onChange={(e) =>
                                      setQuizTitleDraft(
                                        e.target.value
                                      )
                                    }
                                    onBlur={
                                      saveQuizTitle
                                    }
                                    onKeyDown={
                                      handleQuizTitleKeyDown
                                    }
                                    autoFocus
                                    onClick={(e) =>
                                      e.stopPropagation()
                                    }
                                  />
                                ) : isEditingThisItem && item.tipo === "EXAMEN" ? (
                                  <input
                                    className="cc-inline-input"
                                    value={
                                      examTitleDraft
                                    }
                                    onChange={(e) =>
                                      setExamTitleDraft(
                                        e.target.value
                                      )
                                    }
                                    onBlur={
                                      saveExamTitle
                                    }
                                    onKeyDown={
                                      handleExamTitleKeyDown
                                    }
                                    autoFocus
                                    onClick={(e) =>
                                      e.stopPropagation()
                                    }
                                  />
                                ) : (
                                  <h3
                                    className="cc-lesson-title"
                                    onDoubleClick={(
                                      e
                                    ) => {
                                      e.stopPropagation();
                                      if (item.tipo === "LECCION") {
                                        startEditLessonTitle(
                                          modulo.id,
                                          item.id,
                                          item.titulo
                                        );
                                      } else if (item.tipo === "CUESTIONARIO") {
                                        startEditQuizTitle(
                                          modulo.id,
                                          item.id,
                                          item.titulo
                                        );
                                      } else if (item.tipo === "EXAMEN") {
                                        startEditExamTitle(
                                          modulo.id,
                                          item.id,
                                          item.titulo
                                        );
                                      }
                                    }}
                                    title={item.tipo === "LECCION" || item.tipo === "CUESTIONARIO" || item.tipo === "EXAMEN" ? "Doble clic para editar" : ""}
                                  >
                                    {item.tipo === "CUESTIONARIO" && "📝 "}
                                    {item.tipo === "EXAMEN" && "📋 "}
                                    {item.titulo}
                                  </h3>
                                )}
                                {item.monedas > 0 && (
                                  <p className="cc-lesson-meta">
                                    🪙 {item.monedas}{" "}
                                    monedas
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="cc-lesson-actions">
                              {item.tipo === "LECCION" && canShowViewButton(item) && (unlocked ? (
                                <button
                                  type="button"
                                  className="cc-lesson-btn cc-lesson-btn-ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const lessonId = item.idLeccion || item.id;
                                    navigate(`/menu/lms/curso-estudiante/${slug}/player/${lessonId}`);
                                  }}
                                  title="Ver lección"
                                >
                                  <FiEye /> Ver
                                </button>
                              ) : (
                                <span style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  fontWeight: 'bold',
                                  padding: '0.25rem 0.5rem'
                                }}>
                                  🔒 Bloqueado
                                </span>
                              ))}

                              {item.tipo === "CUESTIONARIO" && (
                                canShowViewButton(item) ? (
                                  unlocked ? (
                                    <button
                                      type="button"
                                      className="cc-lesson-btn cc-lesson-btn-ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const quizId = item.idCuestionario || item.id;
                                        navigate(`/menu/lms/curso-estudiante/${slug}/player/${quizId}?type=quiz`);
                                      }}
                                      title="Ver cuestionario"
                                    >
                                      <FiEye /> Ver
                                    </button>
                                  ) : (
                                    <span style={{
                                      fontSize: '0.75rem',
                                      color: '#6b7280',
                                      fontWeight: 'bold',
                                      padding: '0.25rem 0.5rem'
                                    }}>
                                      🔒 Bloqueado
                                    </span>
                                  )
                                ) : (
                                  <span style={{
                                    fontSize: '0.75rem',
                                    color: '#dc2626',
                                    fontWeight: 'bold',
                                    padding: '0.25rem 0.5rem'
                                  }}>
                                    🔒 Límite alcanzado
                                  </span>
                                )
                              )}

                              {item.tipo === "EXAMEN" && (
                                canShowViewButton(item) ? (
                                  unlocked ? (
                                    certificadoGenerado ? (
                                      <span style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        fontWeight: 'bold',
                                        padding: '0.25rem 0.5rem'
                                      }}>
                                        🔒 Certificado generado
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        className="cc-lesson-btn cc-lesson-btn-ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const examId = item.idExamen || item.id;
                                          navigate(`/menu/lms/curso-estudiante/${slug}/player/${examId}?type=exam`);
                                        }}
                                        title="Ver examen"
                                      >
                                        <FiEye /> Ver
                                      </button>
                                    )
                                  ) : (
                                    <span style={{
                                      fontSize: '0.75rem',
                                      color: '#6b7280',
                                      fontWeight: 'bold',
                                      padding: '0.25rem 0.5rem'
                                    }}>
                                      🔒 Bloqueado
                                    </span>
                                  )
                                ) : (
                                  <span style={{
                                    fontSize: '0.75rem',
                                    color: '#dc2626',
                                    fontWeight: 'bold',
                                    padding: '0.25rem 0.5rem'
                                  }}>
                                    🔒 Límite alcanzado
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Botón agregar módulo omitido en vista estudiante (solo lectura) */}
        </div>

        {/* SIDEBAR / Resumen */}
        <aside className="cc-sidebar">
          <div className="cc-summary-card">
            <div className="cc-summary-status-pill">
              En curso
            </div>
            <h3 className="cc-summary-title cc-summary-title-student">
              Resumen del curso
            </h3>

            <div className="cc-summary-grid">
              <div className="cc-summary-item">
                <small className="cc-summary-label">
                  Duración
                </small>
                <strong className="cc-summary-value">
                  {course.duracion || 0} días
                </strong>
              </div>
              <div className="cc-summary-item">
                <small className="cc-summary-label">
                  Evaluaciones
                </small>
                <strong className="cc-summary-value">
                  {stats.totalExams}
                </strong>
              </div>
              <div className="cc-summary-item">
                <small className="cc-summary-label">
                  Módulos
                </small>
                <strong className="cc-summary-value">
                  {stats.totalModules}
                </strong>
              </div>
              <div className="cc-summary-item">
                <small className="cc-summary-label">
                  Lecciones
                </small>
                <strong className="cc-summary-value">
                  {stats.totalLessons}
                </strong>
              </div>
              <div className="cc-summary-item">
                <small className="cc-summary-label">
                  Cuestionarios
                </small>
                <strong className="cc-summary-value">
                  {stats.totalQuizzes}
                </strong>
              </div>
            </div>

            {/* Separador visual */}
            <hr style={{ margin: '1.5rem 0', border: '0', borderTop: '1px solid #e2e8f0' }} />

            {/* Calificación Grande y Centrada */}
            <div style={{ textAlign: 'center', margin: '1.5rem 0' }}>
              <div style={{
                color: '#64748b',
                fontSize: '0.9rem',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                TU CALIFICACIÓN
              </div>
              <div style={{
                fontSize: '3rem',
                fontWeight: '700',
                color: '#0f172a',
                lineHeight: 1
              }}>
                {averageExamGrade.totalExamenes > 0
                  ? `${parseFloat((Number(averageExamGrade.promedio) / 10).toFixed(1))}/10`
                  : "0/10"}
              </div>
            </div>

            {/* Sección: Progreso del curso */}
            <br />
            <div style={{
              textAlign: 'center',
              color: '#64748b',
              fontSize: '0.9rem',
              marginBottom: '1rem',
              fontWeight: '500',
              textTransform: 'uppercase'
            }}>
              PROGRESO DEL CURSO
            </div>

            {/* Barra de progreso */}
            <div className="cc-summary-progress" style={{ marginTop: '0' }}>
              <div className="cc-summary-bar" style={{ height: '10px', borderRadius: '5px' }}>
                <div
                  className="cc-summary-bar-fill"
                  style={{
                    width: `${courseProgress.porcentaje}%`,
                    borderRadius: '5px',
                    transition: 'width 1s ease-in-out'
                  }}
                />
              </div>
              <small className="cc-summary-label cc-summary-progress-text" style={{ textAlign: 'center', display: 'block', marginTop: '0.5rem' }}>
                {courseProgress.completadas} de {courseProgress.total} completadas ({courseProgress.porcentaje}%)
              </small>
            </div>

            {/* Botón principal (Solo si NO hay certificado generado, NO está al 100% y NO ha reprobado definitivamente) */}
            {!certificadoGenerado && courseProgress.porcentaje < 100 && !isCourseFailedDefinitively && (
              <button
                type="button"
                className="cc-summary-btn-main"
                onClick={handleContinueCourse}
              >
                Continuar curso
              </button>
            )}

            {/* Mostrar botón de Generar si está al 100% (o showGenerateCertificateButton true) y no generado */}
            {((showGenerateCertificateButton || courseProgress.porcentaje >= 100) && !certificadoGenerado && !isCourseFailedDefinitively) && (
              <button
                type="button"
                className="cc-summary-btn-main"
                onClick={() => setCertificateCongratsOpen(true)}
              >
                Generar Certificación
              </button>
            )}

            {/* Botón de Recursar Curso si reprobó definitivamente */}
            {isCourseFailedDefinitively && !certificadoGenerado && (
              <button
                type="button"
                className="cc-summary-btn-main"
                style={{ backgroundColor: 'var(--color-primary)', marginTop: '1rem' }}
                onClick={handleReiniciarProgreso}
              >
                Recursar Curso
              </button>
            )}
          </div>
        </aside>
      </div>

      {certificateCongratsOpen && !certificadoGenerado && (
        <div
          className="roles-confirm-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setCertificateCongratsOpen(false);
              setCertificateCongratsDismissed(true);
            }
          }}
        >
          <div className="roles-confirm-card" style={{ width: 'min(720px, 96vw)' }}>
            <div className="roles-topbar" style={{ justifyContent: 'space-between' }}>
              <h3 className="roles-title" style={{ marginRight: 0 }}>
                ¡Felicidades!
              </h3>
              <button
                className="roles-btn"
                onClick={() => {
                  setCertificateCongratsOpen(false);
                  setCertificateCongratsDismissed(true);
                }}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '1rem 1.25rem' }}>
              <p style={{ marginTop: 0 }}>
                Terminaste el curso y tu calificación es aprobatoria.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                <strong>
                  Calificación Final: {Number(averageExamGrade.promedio || 0).toFixed(2)}%
                </strong>
              </p>

              <hr style={{ borderColor: '#e2e8f0', margin: '1rem 0' }} />

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Califica este curso <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setUserRating(star)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: star <= userRating ? '#fbbf24' : '#cbd5e1', // Ambar vs Gris
                        padding: 0
                      }}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Comentario (Opcional)
                </label>
                <textarea
                  rows={3}
                  value={userComment}
                  onChange={(e) => setUserComment(e.target.value)}
                  placeholder="Cuéntanos qué te pareció el curso..."
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '.6rem',
              padding: '0 1.25rem 1rem 1.25rem'
            }}>
              <button
                className="roles-btn"
                onClick={() => {
                  setCertificateCongratsOpen(false);
                  setCertificateCongratsDismissed(true);
                }}
              >
                Más tarde
              </button>
              <button
                className="btn-action"
                onClick={handleSubmitRatingAndCertificate}
                disabled={userRating === 0 || ratingSubmitting}
                style={{ opacity: (userRating === 0 || ratingSubmitting) ? 0.6 : 1 }}
              >
                {ratingSubmitting ? 'Generando...' : 'Obtener Certificado'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Diálogos de confirmación de eliminación */}
      <ConfirmDeleteDialog
        open={deleteModuleDialog.open}
        onClose={() =>
          setDeleteModuleDialog({
            open: false,
            moduleId: null,
            moduleName: "",
          })
        }
        onConfirm={confirmDeleteModule}
        roleName={deleteModuleDialog.moduleName}
      />

      <ConfirmDeleteDialog
        open={deleteLessonDialog.open}
        onClose={() =>
          setDeleteLessonDialog({
            open: false,
            moduleId: null,
            lessonId: null,
            lessonName: "",
          })
        }
        onConfirm={confirmDeleteLesson}
        roleName={deleteLessonDialog.lessonName}
      />

      <ConfirmDeleteDialog
        open={deleteQuizDialog.open}
        onClose={() =>
          setDeleteQuizDialog({
            open: false,
            moduleId: null,
            quizId: null,
            quizName: "",
          })
        }
        onConfirm={confirmDeleteQuiz}
        roleName={deleteQuizDialog.quizName}
      />

      <ConfirmDeleteDialog
        open={deleteExamDialog.open}
        onClose={() =>
          setDeleteExamDialog({
            open: false,
            moduleId: null,
            examId: null,
            examName: "",
          })
        }
        onConfirm={confirmDeleteExam}
        roleName={deleteExamDialog.examName}
      />

      {/* MODAL NUEVO CONTENIDO */}
      {newContentModal.open && (
        <div className="cc-course-modal-overlay">
          <div className="cc-course-modal cc-course-modal-small">
            <h3 className="cc-course-modal-title">
              Agregar contenido al módulo
            </h3>
            <p className="cc-course-modal-subtitle">
              Elige qué tipo de elemento quieres sumar a este
              módulo. Puedes mezclar lecciones, cuestionarios y
              evaluaciones.
            </p>

            <div className="cc-newcontent-grid">
              <button
                type="button"
                className="cc-newcontent-option"
                onClick={() =>
                  handleCreateContent("LECCION")
                }
              >
                <div className="cc-newcontent-icon-wrapper">
                  <FiFileText className="cc-newcontent-icon" />
                </div>
                <div className="cc-newcontent-text">
                  <span className="cc-newcontent-title">
                    Lección
                  </span>
                  <span className="cc-newcontent-subtitle">
                    Contenido explicativo en video, texto o
                    recurso multimedia.
                  </span>
                </div>
              </button>

              <button
                type="button"
                className="cc-newcontent-option"
                onClick={() =>
                  handleCreateContent("CUESTIONARIO")
                }
              >
                <div className="cc-newcontent-icon-wrapper">
                  <FiHelpCircle className="cc-newcontent-icon" />
                </div>
                <div className="cc-newcontent-text">
                  <span className="cc-newcontent-title">
                    Cuestionario
                  </span>
                  <span className="cc-newcontent-subtitle">
                    Quiz corto para reforzar conceptos (sin
                    valor final).
                  </span>
                </div>
              </button>

              <button
                type="button"
                className="cc-newcontent-option"
                onClick={() =>
                  handleCreateContent("EVALUACION")
                }
              >
                <div className="cc-newcontent-icon-wrapper">
                  <FiClipboard className="cc-newcontent-icon" />
                </div>
                <div className="cc-newcontent-text">
                  <span className="cc-newcontent-title">
                    Evaluación
                  </span>
                  <span className="cc-newcontent-subtitle">
                    Examen con peso en la calificación del
                    curso.
                  </span>
                </div>
              </button>
            </div>

            <div className="cc-course-modal-actions cc-newcontent-actions">
              <button
                type="button"
                className="cc-btn-secondary"
                onClick={closeNewContentModal}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => {
          if (alertDialog.onConfirm) {
            alertDialog.onConfirm();
          }
          setAlertDialog({ ...alertDialog, open: false });
        }}
        onCancel={alertDialog.onCancel || (() => setAlertDialog({ ...alertDialog, open: false }))}
        title={alertDialog.title}
        message={alertDialog.message}
        variant={alertDialog.variant}
        buttonText={alertDialog.buttonText || 'Aceptar'}
        cancelText={alertDialog.cancelText || 'Cancelar'}
      />
    </div>
  );
};

export default CourseCurriculum;
